import React, { useEffect, useRef } from 'react';
import { paymentService } from '../../services/paymentService';
import { toastHelper } from '../../utils/toastHelper';
import './PayPalButton.css';

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: {
        createSubscription: (data: any, actions: any) => Promise<any>;
        onApprove: (data: any, actions: any) => void;
        style?: {
          layout?: string;
          color?: string;
          shape?: string;
          label?: string;
        };
      }) => {
        render: (selector: string) => void;
      };
    };
  }
}

interface PayPalButtonProps {
  planId: string;
  containerId: string;
  tier: string;
  billingCycle: string;
  onSubscriptionSuccess?: () => void;
}

const PayPalButton: React.FC<PayPalButtonProps> = ({
  planId,
  containerId,
  tier,
  billingCycle,
  onSubscriptionSuccess,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<boolean>(false);
  // Store session token for webhook to find user (important for mobile redirect flow)
  const sessionTokenRef = useRef<string | null>(null);

  // Diagnostic: Check if we're returning from a PayPal flow that didn't complete
  useEffect(() => {
    const flowStarted = localStorage.getItem('paypal_flow_started');
    const onApproveFired = localStorage.getItem('paypal_onApprove_fired');
    const sessionToken = localStorage.getItem('paypal_pending_session');

    if (flowStarted && !onApproveFired) {
      const startTime = new Date(flowStarted);
      const now = new Date();
      const elapsedMinutes = (now.getTime() - startTime.getTime()) / 1000 / 60;

      // If flow started more than 2 minutes ago and onApprove never fired
      if (elapsedMinutes > 2) {
        console.warn('[PayPal] Detected incomplete flow', {
          flowStarted,
          elapsedMinutes,
          sessionToken,
          message: 'onApprove callback never fired.'
        });

        // Log this to backend
        paymentService.logPayPalFlowEvent({
          event_type: 'incomplete_flow_detected',
          session_token: sessionToken || undefined,
          metadata: {
            flow_started: flowStarted,
            elapsed_minutes: elapsedMinutes,
            reason: 'onApprove callback never fired - likely mobile tab suspension or browser issue'
          }
        }).catch(() => {}); // Non-critical logging

        // Clean up old tracking
        localStorage.removeItem('paypal_flow_started');
        localStorage.removeItem('paypal_pending_session');
      }
    }

    // Clean up completed flows
    if (onApproveFired) {
      localStorage.removeItem('paypal_onApprove_fired');
      localStorage.removeItem('paypal_onApprove_time');
      localStorage.removeItem('paypal_flow_started');
      localStorage.removeItem('paypal_pending_session');
    }
  }, []);

  useEffect(() => {
    if (!window.paypal || renderedRef.current) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Skip if container is not visible (in hidden tab)
    if (container.offsetParent === null) {
      return;
    }

    // Ensure container clips content during initial render (CSS handles this)
    // Remove loaded class to reset overflow to hidden
    container.classList.remove('paypal-loaded');

    try {
      const buttonInstance = window.paypal.Buttons({
        createSubscription: async function (data: any, actions: any) {
          console.log('[PayPal] createSubscription called', { planId, tier, billingCycle });

          try {
            // Create pending subscription to link user with PayPal subscription
            // This is critical for mobile where redirect flow loses the onApprove callback
            const pendingResult = await paymentService.createPendingSubscription(planId);
            sessionTokenRef.current = pendingResult.session_token;

            console.log('[PayPal] Pending subscription created', {
              session_token: pendingResult.session_token
            });

            // Store in localStorage as backup for debugging
            localStorage.setItem('paypal_pending_session', pendingResult.session_token);
            localStorage.setItem('paypal_flow_started', new Date().toISOString());

            // Log flow start to backend
            paymentService.logPayPalFlowEvent({
              event_type: 'flow_started',
              session_token: pendingResult.session_token,
              plan_id: planId,
              tier,
              billing_cycle: billingCycle,
              metadata: {
                timestamp: new Date().toISOString()
              }
            }).catch(() => {}); // Non-critical logging

            return actions.subscription.create({
              plan_id: planId,
              custom_id: pendingResult.session_token, // Allows webhook to find user
            });
          } catch (error) {
            console.error('[PayPal] Failed to create pending subscription:', error);
            // Fallback: create without custom_id (webhook backup won't work, but onApprove still will)
            return actions.subscription.create({
              plan_id: planId,
            });
          }
        },
        onApprove: async function (data: any) {
          const subscriptionId = data.subscriptionID;
          const timestamp = new Date().toISOString();

          console.log('[PayPal] onApprove triggered!', {
            subscriptionID: subscriptionId,
            timestamp,
            session_token: sessionTokenRef.current
          });

          // Track that callback fired
          localStorage.setItem('paypal_onApprove_fired', 'true');
          localStorage.setItem('paypal_onApprove_time', timestamp);

          // Log to backend
          paymentService.logPayPalFlowEvent({
            event_type: 'onApprove_fired',
            subscription_id: subscriptionId,
            session_token: sessionTokenRef.current || undefined,
            plan_id: planId,
            tier,
            billing_cycle: billingCycle,
            metadata: {
              timestamp
            }
          }).catch(() => {}); // Non-critical logging

          try {
            console.log('[PayPal] Calling verifySubscriptionActivation...', { subscriptionId });
            const verifyResult = await paymentService.verifySubscriptionActivation(subscriptionId);
            console.log('[PayPal] Verification result:', verifyResult);

            // Log verification success
            paymentService.logPayPalFlowEvent({
              event_type: 'verify_success',
              subscription_id: subscriptionId,
              session_token: sessionTokenRef.current || undefined,
              plan_id: planId,
              tier,
              billing_cycle: billingCycle,
              metadata: {
                verify_result: verifyResult,
                timestamp: new Date().toISOString()
              }
            }).catch(() => {}); // Non-critical logging

            if (verifyResult.status === 'active') {
              console.log('[PayPal] Subscription active, showing success toast');
              toastHelper.newMessage(
                'success',
                'Success',
                `You have successfully subscribed to ${tier} ${billingCycle} plan!`
              );
              // Reload page after short delay to show updated subscription
              setTimeout(() => {
                console.log('[PayPal] Reloading page...');
                window.location.reload();
              }, 1500);
            } else {
              // Rare: PayPal returned but subscription not yet active
              console.log('[PayPal] Subscription not yet active, refreshing...', verifyResult);
              toastHelper.newMessage(
                'info',
                'Processing',
                'Your subscription is being activated. Refreshing...'
              );
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            }
          } catch (error) {
            console.error('[PayPal] Subscription verification error:', error);
            const errorMessage = String(error);

            localStorage.setItem('paypal_verify_error', JSON.stringify({
              error: errorMessage,
              timestamp: new Date().toISOString()
            }));

            // Log verification error
            paymentService.logPayPalFlowEvent({
              event_type: 'verify_error',
              subscription_id: subscriptionId,
              session_token: sessionTokenRef.current || undefined,
              plan_id: planId,
              tier,
              billing_cycle: billingCycle,
              error_message: errorMessage,
              metadata: {
                timestamp: new Date().toISOString()
              }
            }).catch(() => {}); // Non-critical logging

            toastHelper.newMessage(
              'error',
              'Error',
              'Failed to verify subscription. Refreshing...'
            );
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        },
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'subscribe',
        },
      });
      
      // Render the buttons (render() returns a Promise)
      buttonInstance.render(`#${containerId}`)
        .then(() => {
          renderedRef.current = true;
        })
        .catch((err: Error) => {
          // PayPal SDK internal errors - these are often benign (e.g., container not visible)
          console.debug('[PayPal] Button render issue:', err?.message || err);
        });
      renderedRef.current = true; // Mark as rendered to prevent duplicate attempts
      
      // Wait for PayPal to load, then fade in
      const checkAndShow = () => {
        const iframe = container.querySelector('iframe');
        const paypalDiv = container.querySelector('[class*="paypal-buttons"]') as HTMLElement;
        
        if (iframe || paypalDiv) {
          // Fix PayPal height: 0px bug
          if (paypalDiv) {
            paypalDiv.style.setProperty('height', 'auto', 'important');
            paypalDiv.style.setProperty('min-height', '50px', 'important');
            // Ensure buttons render in place, not offset
            paypalDiv.style.setProperty('position', 'relative', 'important');
            paypalDiv.style.setProperty('top', '0', 'important');
            paypalDiv.style.setProperty('left', '0', 'important');
            paypalDiv.style.setProperty('transform', 'none', 'important');
            paypalDiv.style.setProperty('margin', '0', 'important');
          }
          
          // Ensure iframe is positioned correctly
          if (iframe && iframe instanceof HTMLIFrameElement) {
            iframe.style.setProperty('position', 'relative', 'important');
            iframe.style.setProperty('top', '0', 'important');
            iframe.style.setProperty('left', '0', 'important');
            iframe.style.setProperty('transform', 'none', 'important');
            iframe.style.setProperty('margin', '0', 'important');
          }
          
          // Small delay to ensure PayPal has finished positioning, then allow overflow
          setTimeout(() => {
            container.classList.add('paypal-loaded');
          }, 100);
          return true;
        }
        return false;
      };

      // Check immediately, then every 100ms until loaded (max 2 seconds)
      let attempts = 0;
      const maxAttempts = 20;
      const checkInterval = setInterval(() => {
        attempts++;
        if (checkAndShow() || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          if (attempts >= maxAttempts) {
            // Show anyway after timeout
            container.classList.add('paypal-loaded');
          }
        }
      }, 100);
    } catch (err) {
      console.error('Failed to render PayPal button:', err);
      // Show container even on error
      if (container) {
        container.classList.add('paypal-loaded');
      }
    }
  }, [planId, containerId, tier, billingCycle, onSubscriptionSuccess]);

  return (
    <div
      id={containerId}
      ref={containerRef}
      className="paypal-button-container mt-auto w-full"
      style={{ colorScheme: 'none' }}
    />
  );
};

export default PayPalButton;

